import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MaskedHeading, Rise } from "@/components/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", practice: "", qualifications: "", motivation: "" };
const muted = { color: "var(--kh-text-muted)" };

const Field = ({ label, id, textarea, ...props }) => (
  <div>
    <label htmlFor={id} className="kh-mono block mb-2">{label}</label>
    {textarea ? <textarea id={id} className="kh-input" data-testid={`apply-form-${id}-input`} {...props} />
      : <input id={id} className="kh-input" data-testid={`apply-form-${id}-input`} {...props} />}
  </div>
);

const Success = () => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="kh-card" data-testid="apply-success">
    <div className="kh-eyebrow mb-4">Received</div>
    <h2 className="kh-serif text-4xl leading-tight">Thank you. Your enquiry is with the council.</h2>
    <p className="mt-5 leading-relaxed" style={muted}>If it reads well, we will invite you to a discovery interview.</p>
  </motion.div>
);

export default function ApplyPage() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/applications`, form);
      setDone(true);
      toast.success("Application submitted");
    } catch {
      toast.error("Could not submit. Please check your details and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="apply-page">
      <section className="px-6 sm:px-10 lg:px-14 pt-20 lg:pt-28 pb-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Rise><div className="kh-eyebrow mb-6">One-to-one training</div></Rise>
            <MaskedHeading text="Apply to train." className="kh-serif text-5xl sm:text-6xl leading-[1]" />
            <Rise delay={0.5}><p className="mt-6 text-lg max-w-md leading-relaxed" style={muted}>A short enquiry. If it reads well we invite you to a discovery interview.</p></Rise>
            <motion.img src="/images/apply-desk.jpg" alt="Laptop on a desk in a warm, lamp-lit room" data-testid="apply-image" className="mt-10 w-full aspect-[4/5] object-cover hidden lg:block" style={{ borderRadius: "var(--kh-radius)" }}
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }} animate={{ clipPath: "inset(0% 0% 0% 0%)" }} transition={{ duration: 1.3, delay: 0.4, ease: [0.7, 0, 0.2, 1] }} />
          </div>
          <Rise delay={0.2} className="lg:col-span-7">
            {done ? <Success /> : (
              <form onSubmit={submit} className="kh-card space-y-6" data-testid="apply-form">
                <Field label="Full name *" id="name" required value={form.name} onChange={set("name")} />
                <Field label="Email *" id="email" type="email" required value={form.email} onChange={set("email")} />
                <Field label="Current practice & registration body *" id="practice" required placeholder="e.g. UKCP registered, private trauma practice, 6 years" value={form.practice} onChange={set("practice")} />
                <Field label="Qualifications" id="qualifications" textarea rows={3} value={form.qualifications} onChange={set("qualifications")} />
                <Field label="Why Kheiron? *" id="motivation" textarea rows={5} required placeholder="Please write freely — this is the only part we read in full before interview." value={form.motivation} onChange={set("motivation")} />
                <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <p className="text-xs" style={muted}>Applications are reviewed in two rounds. No automated scoring is used.</p>
                  <button type="submit" disabled={sending} className="kh-btn-primary" data-testid="apply-form-submit-button">
                    {sending ? "Submitting…" : "Submit application"} <span className="kh-arrow" aria-hidden="true">→</span>
                  </button>
                </div>
              </form>
            )}
          </Rise>
        </div>
      </section>
    </main>
  );
}
